import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Icon } from '@iconify/react';
import { SOCIETY_PORTAL, SOCIETY_PORTAL_RECEIPTS } from '../../../../Routing/routes';
import { bulkUploadReceipts, clearReceiptBulkUploadState } from '../../../../redux/actions/SocietyPortal/ReceiptAction/ReceiptAction';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import { getSocietyBanksDropdown } from '../../../../redux/actions/SocietyPortal/SocietyBankAction/SocietyBankAction';
import * as XLSX from 'xlsx';

const ALLOWED_EXTENSIONS = ['xlsx', 'xls'];

const formatCurrency = (amount) =>
    `Rs ${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STEPS = [
    { num: '01', label: 'UPLOAD', desc: 'Upload your file' },
    { num: '02', label: 'RESULT', desc: 'Import result' },
];

const BulkUploadReceipts = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [currentStep, setCurrentStep] = useState(0);
    const [file, setFile] = useState(null);
    const [fileError, setFileError] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef(null);

    const { bulkUploadLoading, bulkUploadResult } = useSelector(state => state.ReceiptReducer || {});
    // A non-cash row must name one of the society's bank accounts exactly, so the
    // template carries the list: there is no bank picker on an uploaded receipt.
    const { banksDropdown } = useSelector(state => state.SocietyBankReducer || {});
    const societyId = selectedSocietyStorer.getSocietyId();
    const bankNames = (Array.isArray(banksDropdown) ? banksDropdown : [])
        .map(b => b.bank_name || b.label || b.name)
        .filter(Boolean);

    useEffect(() => {
        if (societyId) dispatch(getSocietyBanksDropdown({ society_id: societyId }));
        return () => { dispatch(clearReceiptBulkUploadState()); };
    }, [dispatch, societyId]);

    const goBack = () => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_RECEIPTS);

    const handleFile = (selectedFile) => {
        if (!selectedFile) return;
        const ext = selectedFile.name.split('.').pop().toLowerCase();
        if (!ALLOWED_EXTENSIONS.includes(ext)) {
            setFileError('Only Excel files (.xls, .xlsx) are supported.');
            setFile(null);
            return;
        }
        setFileError(null);
        setFile(selectedFile);
    };

    const handleDrop = (e) => { e.preventDefault(); setIsDragging(false); handleFile(e.dataTransfer.files[0]); };
    const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
    const handleDragLeave = () => setIsDragging(false);
    const handleInputChange = (e) => handleFile(e.target.files[0]);
    const handleRemoveFile = () => { setFile(null); setFileError(null); if (fileInputRef.current) fileInputRef.current.value = ''; };

    const formatSize = (bytes) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    const handleDownloadTemplate = () => {
        // The four tds_* columns are optional; tds_gl_code is required only when
        // tds_deducted is given, exactly as on the Create Receipt form.
        const headers = ['wing_title', 'flat_number', 'payment_date', 'amount', 'payment_mode', 'reference_no', 'bank_name', 'instrument_date', 'remarks', 'receipt_type', 'tds_deducted', 'tds_section', 'tds_rate', 'tds_gl_code'];
        const firstBank = bankNames[0] || 'SBI';
        const bankNote = bankNames.length
            ? `Required for every non-CASH row. Use one of: ${bankNames.join(' / ')}`
            : 'Required for every non-CASH row — add a bank under Setup > Bank Accounts first';
        const sampleRow = ['A', '101', '2026-03-15', '5000', 'NEFT', 'REF001', firstBank, '', 'March payment', 'PAYMENT', '', '', '', ''];
        const notesRow = ['', '', 'YYYY-MM-DD', '', 'CASH / CHEQUE / NEFT / RTGS / UPI / CARD / DD / OTHER', '', bankNote, 'YYYY-MM-DD', '', 'PAYMENT / ADVANCE', 'Optional — amount the member withheld as TDS', 'e.g. 194C', 'Percent, e.g. 2', 'Required if tds_deducted is given — TDS receivable GL code'];
        const ws = XLSX.utils.aoa_to_sheet([headers, sampleRow, notesRow]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Receipts');
        // A second sheet listing the society's banks, to copy from.
        const banksSheet = XLSX.utils.aoa_to_sheet([
            ['bank_name (copy one of these into the bank_name column)'],
            ...(bankNames.length ? bankNames.map(n => [n]) : [['No bank accounts yet — add one under Setup > Bank Accounts']])
        ]);
        XLSX.utils.book_append_sheet(wb, banksSheet, 'Banks');
        XLSX.writeFile(wb, 'receipt_bulk_upload_sample.xlsx');
    };

    const handleUpload = () => {
        if (!file) return;
        dispatch(bulkUploadReceipts({
            society_id: societyId,
            file,
            onSuccess: () => setCurrentStep(1),
        }));
    };

    const created = bulkUploadResult?.created || [];
    const failed = bulkUploadResult?.failed || [];
    const allSuccess = failed.length === 0;

    return (
        <div className="tw-bg-gray-50 tw-min-h-screen">
            {/* Header Bar */}
            <div className="tw-bg-white tw-border-b tw-border-gray-200 tw-px-5 tw-relative" style={{ height: 60 }}>
                <div className="tw-absolute tw-left-5 tw-top-1/2 tw--translate-y-1/2 tw-flex tw-items-center tw-gap-3">
                    <button onClick={goBack} className="tw-p-1 tw-rounded hover:tw-bg-gray-100 tw-text-gray-400 tw-flex tw-items-center tw-justify-center">
                        <Icon icon="mdi:arrow-left" className="tw-text-lg" />
                    </button>
                    <div className="tw-leading-tight">
                        <p className="tw-text-[10px] tw-font-semibold tw-text-blue-600 tw-uppercase tw-tracking-wider tw-m-0">Bulk Upload</p>
                        <h1 className="tw-text-[15px] tw-font-bold tw-text-gray-900 tw-m-0 tw-mt-px">Receipts</h1>
                    </div>
                </div>

                {/* Stepper */}
                <div className="tw-absolute tw-left-1/2 tw-top-1/2 tw--translate-x-1/2 tw--translate-y-1/2 tw-flex tw-items-center">
                    {STEPS.map((step, index) => (
                        <div key={step.label} className="tw-flex tw-items-center">
                            <div className="tw-flex tw-items-center tw-gap-2.5">
                                <div style={{ width: 34, height: 34 }} className={`tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-xs tw-font-semibold tw-flex-shrink-0 ${
                                    index < currentStep
                                        ? 'tw-bg-emerald-500 tw-text-white'
                                        : index === currentStep
                                            ? 'tw-bg-indigo-600 tw-text-white'
                                            : 'tw-bg-white tw-text-gray-400 tw-border tw-border-gray-300'
                                }`}>
                                    {index < currentStep ? <Icon icon="mdi:check" className="tw-text-sm" /> : step.num}
                                </div>
                                <div>
                                    <p className={`tw-text-xs tw-font-semibold tw-m-0 ${index <= currentStep ? 'tw-text-gray-700' : 'tw-text-gray-400'}`}>{step.label}</p>
                                    <p className="tw-text-[10px] tw-text-gray-400 tw-m-0 tw-font-normal">{step.desc}</p>
                                </div>
                            </div>
                            {index < STEPS.length - 1 && (
                                <div className={`tw-w-28 tw-mx-5 tw-border-t tw-border-dashed ${index < currentStep ? 'tw-border-emerald-400' : 'tw-border-gray-300'}`} />
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <div className="tw-p-6">
                {/* Step 1: Upload */}
                {currentStep === 0 && (
                    <div>
                        <div className="tw-bg-white tw-rounded-xl tw-shadow-sm tw-border tw-border-gray-100 tw-overflow-hidden">
                            <div className="tw-grid tw-grid-cols-2">
                                {/* Left Side */}
                                <div className="tw-p-10 tw-flex tw-flex-col tw-justify-center tw-border-r tw-border-gray-100">
                                    <p className="tw-text-sm tw-text-gray-400 tw-font-medium">Seamless</p>
                                    <h2 className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mt-0.5">Receipt Import</h2>
                                    <p className="tw-text-xs tw-text-gray-400 tw-mt-3 tw-leading-relaxed">
                                        Upload an Excel file with receipt data. Allocations are auto-calculated against outstanding invoices and debit notes.
                                    </p>
                                    <ul className="tw-mt-5 tw-space-y-3">
                                        <li className="tw-flex tw-items-center tw-gap-2">
                                            <div className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-indigo-500" />
                                            <span className="tw-text-sm tw-font-medium tw-text-gray-700">Auto allocation to outstanding bills</span>
                                        </li>
                                        <li className="tw-flex tw-items-center tw-gap-2">
                                            <div className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-indigo-500" />
                                            <span className="tw-text-sm tw-font-medium tw-text-gray-700">Component breakdown (Principal, Interest, etc.)</span>
                                        </li>
                                        <li className="tw-flex tw-items-center tw-gap-2">
                                            <div className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-indigo-500" />
                                            <span className="tw-text-sm tw-font-medium tw-text-gray-700">CHEQUE/DD receipts auto-set to PENDING</span>
                                        </li>
                                    </ul>

                                    <div className="tw-mt-6 tw-p-3 tw-bg-gray-50 tw-rounded-lg tw-border tw-border-gray-100">
                                        <p className="tw-text-[10px] tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider tw-mb-2">Required Columns</p>
                                        <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                                            {['wing_title', 'flat_number', 'payment_date', 'amount', 'payment_mode'].map(col => (
                                                <span key={col} className="tw-px-2 tw-py-0.5 tw-bg-white tw-border tw-border-gray-200 tw-rounded tw-text-[10px] tw-font-medium tw-text-gray-600">{col}</span>
                                            ))}
                                        </div>
                                        <p className="tw-text-[10px] tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider tw-mb-2 tw-mt-3">Optional Columns</p>
                                        <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                                            {['reference_no', 'bank_name', 'instrument_date', 'remarks', 'receipt_type'].map(col => (
                                                <span key={col} className="tw-px-2 tw-py-0.5 tw-bg-white tw-border tw-border-gray-200 tw-rounded tw-text-[10px] tw-font-medium tw-text-gray-400">{col}</span>
                                            ))}
                                        </div>
                                        <p className="tw-text-[10px] tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider tw-mb-2 tw-mt-3">Payment Mode</p>
                                        <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                                            {['CASH', 'CHEQUE', 'NEFT', 'RTGS', 'UPI', 'CARD', 'DD', 'OTHER'].map(val => (
                                                <span key={val} className="tw-px-2 tw-py-0.5 tw-bg-blue-50 tw-border tw-border-blue-100 tw-rounded tw-text-[10px] tw-font-medium tw-text-blue-600">{val}</span>
                                            ))}
                                        </div>
                                        <p className="tw-text-[10px] tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider tw-mb-2 tw-mt-3">Receipt Type</p>
                                        <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                                            {['PAYMENT', 'ADVANCE'].map(val => (
                                                <span key={val} className="tw-px-2 tw-py-0.5 tw-bg-blue-50 tw-border tw-border-blue-100 tw-rounded tw-text-[10px] tw-font-medium tw-text-blue-600">{val}</span>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Right Side - Upload Area */}
                                <div className="tw-p-10 tw-flex tw-flex-col tw-items-center tw-justify-center">
                                    <div
                                        onDrop={handleDrop}
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        onClick={() => fileInputRef.current?.click()}
                                        className={`tw-w-full tw-border-2 tw-border-dashed tw-rounded-xl tw-py-12 tw-px-8 tw-text-center tw-cursor-pointer tw-transition-colors ${
                                            isDragging
                                                ? 'tw-border-indigo-400 tw-bg-indigo-50'
                                                : 'tw-border-gray-200 hover:tw-border-gray-300 tw-bg-gray-50/50'
                                        }`}
                                    >
                                        <div className="tw-w-14 tw-h-14 tw-rounded-full tw-bg-indigo-50 tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-4">
                                            <Icon icon="mdi:cloud-upload-outline" className="tw-text-2xl tw-text-indigo-400" />
                                        </div>
                                        <p className="tw-text-sm tw-font-semibold tw-text-gray-700">Drag files to import</p>
                                        <p className="tw-text-xs tw-text-gray-400 tw-mt-1">
                                            or <span className="tw-text-blue-500 tw-font-medium hover:tw-underline">browse local files</span>
                                        </p>
                                        <div className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-mt-4">
                                            <span className="tw-text-[10px] tw-text-gray-400 tw-uppercase tw-tracking-wider tw-font-medium">XLS / XLSX</span>
                                            <span className="tw-text-gray-300">&middot;</span>
                                            <span className="tw-text-[10px] tw-text-gray-400 tw-uppercase tw-tracking-wider tw-font-medium">Max 50MB</span>
                                        </div>
                                        <input ref={fileInputRef} type="file" accept=".xls,.xlsx" onChange={handleInputChange} className="tw-hidden" />
                                    </div>

                                    {fileError && (
                                        <div className="tw-w-full tw-mt-3 tw-flex tw-items-start tw-gap-2 tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-p-3">
                                            <Icon icon="mdi:alert-circle" className="tw-text-red-500 tw-text-sm tw-mt-0.5 tw-flex-shrink-0" />
                                            <p className="tw-text-xs tw-text-red-600">{fileError}</p>
                                        </div>
                                    )}

                                    {file && !fileError && (
                                        <div className="tw-w-full tw-mt-3 tw-flex tw-items-center tw-justify-between tw-bg-emerald-50 tw-border tw-border-emerald-200 tw-rounded-lg tw-p-3">
                                            <div className="tw-flex tw-items-center tw-gap-2">
                                                <Icon icon="mdi:file-excel-outline" className="tw-text-emerald-500 tw-text-lg" />
                                                <div>
                                                    <p className="tw-text-xs tw-font-medium tw-text-gray-800">{file.name}</p>
                                                    <p className="tw-text-[10px] tw-text-gray-400">{formatSize(file.size)}</p>
                                                </div>
                                            </div>
                                            <button onClick={(e) => { e.stopPropagation(); handleRemoveFile(); }} className="tw-p-1 tw-rounded hover:tw-bg-emerald-100 tw-text-gray-400 hover:tw-text-red-500">
                                                <Icon icon="mdi:close" className="tw-text-sm" />
                                            </button>
                                        </div>
                                    )}

                                    <button
                                        onClick={handleDownloadTemplate}
                                        className="tw-flex tw-items-center tw-gap-1.5 tw-text-blue-500 tw-text-xs tw-font-medium hover:tw-text-blue-600 tw-mt-4"
                                    >
                                        <Icon icon="mdi:download-outline" className="tw-text-base" />
                                        Download Sample Format
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="tw-flex tw-items-center tw-justify-end tw-gap-3 tw-mt-5">
                            <button onClick={goBack} disabled={bulkUploadLoading} className="tw-px-5 tw-py-2 tw-rounded-lg tw-text-xs tw-font-semibold tw-text-gray-500 tw-bg-gray-100 hover:tw-bg-gray-200 disabled:tw-opacity-50 tw-transition-colors">
                                Cancel
                            </button>
                            <button onClick={handleUpload} disabled={!file || !!fileError || bulkUploadLoading} className="tw-px-5 tw-py-2 tw-rounded-lg tw-text-xs tw-font-semibold tw-text-white tw-bg-blue-600 hover:tw-bg-blue-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors tw-flex tw-items-center tw-gap-1.5">
                                {bulkUploadLoading && <Icon icon="mdi:loading" className="tw-text-sm tw-animate-spin" />}
                                <Icon icon="mdi:upload-outline" className="tw-text-sm" />
                                {bulkUploadLoading ? 'Uploading...' : 'Upload & Import'}
                            </button>
                        </div>
                    </div>
                )}

                {/* Step 2: Result */}
                {currentStep === 1 && bulkUploadResult && (
                    <div>
                        {/* Result Summary Card */}
                        <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-p-6 tw-text-center">
                            <div className={`tw-w-14 tw-h-14 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mx-auto ${allSuccess ? 'tw-bg-emerald-50' : 'tw-bg-amber-50'}`}>
                                <Icon icon={allSuccess ? 'mdi:check-circle-outline' : 'mdi:alert-circle-outline'} className={`tw-text-3xl ${allSuccess ? 'tw-text-emerald-500' : 'tw-text-amber-500'}`} />
                            </div>
                            <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mt-3">
                                {allSuccess ? 'Import Completed Successfully' : 'Import Completed with Errors'}
                            </h3>
                            <p className="tw-text-[10px] tw-text-gray-400 tw-mt-1">
                                {allSuccess
                                    ? `All ${created.length} receipts have been created successfully.`
                                    : `${created.length} receipts created, ${failed.length} had errors.`
                                }
                            </p>
                            <div className="tw-flex tw-items-center tw-justify-center tw-gap-5 tw-mt-5">
                                <div className="tw-text-center">
                                    <p className="tw-text-lg tw-font-semibold tw-text-gray-800">{created.length + failed.length}</p>
                                    <p className="tw-text-[10px] tw-text-gray-400 tw-uppercase tw-tracking-wide">Total</p>
                                </div>
                                <div className="tw-w-px tw-h-8 tw-bg-gray-200" />
                                <div className="tw-text-center">
                                    <p className="tw-text-lg tw-font-semibold tw-text-emerald-600">{created.length}</p>
                                    <p className="tw-text-[10px] tw-text-gray-400 tw-uppercase tw-tracking-wide">Created</p>
                                </div>
                                <div className="tw-w-px tw-h-8 tw-bg-gray-200" />
                                <div className="tw-text-center">
                                    <p className="tw-text-lg tw-font-semibold tw-text-red-600">{failed.length}</p>
                                    <p className="tw-text-[10px] tw-text-gray-400 tw-uppercase tw-tracking-wide">Failed</p>
                                </div>
                            </div>
                        </div>

                        {/* Created Receipts Table */}
                        {created.length > 0 && (
                            <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-mt-3 tw-overflow-hidden">
                                <div className="tw-px-3 tw-py-2 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-gap-1.5">
                                    <Icon icon="mdi:check-circle-outline" className="tw-text-emerald-500 tw-text-sm" />
                                    <h4 className="tw-text-xs tw-font-semibold tw-text-gray-800">Created Receipts ({created.length})</h4>
                                </div>
                                <div className="tw-overflow-x-auto">
                                    <table className="tw-w-full tw-text-xs">
                                        <thead>
                                            <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-100">
                                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Receipt No</th>
                                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Flat</th>
                                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Date</th>
                                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Mode</th>
                                                <th className="tw-text-right tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Amount</th>
                                                <th className="tw-text-center tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Allocations</th>
                                                <th className="tw-text-right tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Unallocated</th>
                                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {created.map((r, idx) => (
                                                <tr key={idx} className="tw-border-b tw-border-gray-50">
                                                    <td className="tw-px-3 tw-py-2 tw-text-gray-700 tw-font-medium">{r.receipt_number || r.document_number || '-'}</td>
                                                    <td className="tw-px-3 tw-py-2 tw-text-gray-600">{r.wing_title}-{r.flat_number}</td>
                                                    <td className="tw-px-3 tw-py-2 tw-text-gray-600">{r.payment_date}</td>
                                                    <td className="tw-px-3 tw-py-2">
                                                        <span className="tw-inline-flex tw-items-center tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-medium tw-bg-blue-50 tw-text-blue-600">
                                                            {r.payment_mode}
                                                        </span>
                                                    </td>
                                                    <td className="tw-px-3 tw-py-2 tw-text-right tw-text-emerald-600 tw-font-medium">{formatCurrency(r.amount)}</td>
                                                    <td className="tw-px-3 tw-py-2 tw-text-center tw-text-gray-600">{r.allocation_count ?? '-'}</td>
                                                    <td className="tw-px-3 tw-py-2 tw-text-right tw-text-gray-600">{r.unallocated_amount != null ? formatCurrency(r.unallocated_amount) : '-'}</td>
                                                    <td className="tw-px-3 tw-py-2">
                                                        <span className={`tw-inline-flex tw-items-center tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-medium ${r.status === 'CLEARED' ? 'tw-bg-emerald-50 tw-text-emerald-600' : 'tw-bg-amber-50 tw-text-amber-600'}`}>
                                                            {r.status || '-'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Failed Rows Table */}
                        {failed.length > 0 && (
                            <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-mt-3 tw-overflow-hidden">
                                <div className="tw-px-3 tw-py-2 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-gap-1.5">
                                    <Icon icon="mdi:alert-outline" className="tw-text-red-500 tw-text-sm" />
                                    <h4 className="tw-text-xs tw-font-semibold tw-text-gray-800">Failed Rows ({failed.length})</h4>
                                </div>
                                <div className="tw-overflow-x-auto">
                                    <table className="tw-w-full tw-text-xs">
                                        <thead>
                                            <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-100">
                                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Row</th>
                                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Flat</th>
                                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase">Errors</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {failed.map((f, idx) => (
                                                <tr key={idx} className="tw-border-b tw-border-gray-50 tw-bg-red-50/50">
                                                    <td className="tw-px-3 tw-py-2 tw-text-gray-500">{f.row ?? f.rowNum ?? idx + 1}</td>
                                                    <td className="tw-px-3 tw-py-2 tw-text-gray-700">{f.wing_title && f.flat_number ? `${f.wing_title}-${f.flat_number}` : '-'}</td>
                                                    <td className="tw-px-3 tw-py-2">
                                                        <div className="tw-flex tw-flex-wrap tw-gap-1">
                                                            {(Array.isArray(f.errors) ? f.errors : [f.error || f.message || 'Unknown error']).map((e, i) => (
                                                                <span key={i} className="tw-inline-flex tw-items-center tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[9px] tw-font-medium tw-bg-red-100 tw-text-red-600">
                                                                    {e}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Action */}
                        <div className="tw-flex tw-items-center tw-justify-end tw-mt-4">
                            <button onClick={goBack} className="tw-flex tw-items-center tw-gap-1.5 tw-px-5 tw-py-2 tw-rounded-lg tw-text-xs tw-font-semibold tw-text-white tw-bg-blue-600 hover:tw-bg-blue-700 tw-transition-colors">
                                <Icon icon="mdi:receipt-text-outline" className="tw-text-sm" />
                                Go to Receipts
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BulkUploadReceipts;
